import type * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { matGlass } from "../../engines/materials";
import {
	type PremiumDetailId,
	readPremiumSlot,
} from "../../engines/premiumDetails";
import { SHARED_GEOMETRIES } from "./sceneDisposal";

const loader = new GLTFLoader();
const premiumModuleCache = new Map<string, Promise<THREE.Object3D>>();

const DEFAULT_PREMIUM_SIZES: Record<
	PremiumDetailId,
	{ width: number; height: number; depth: number }
> = {
	"square-front-door": { width: 0.7, height: 1.9, depth: 0.08 },
};

/** Imported glazing must participate in the same per-view quality swap as engine glass. */
export function preparePremiumModule(root: THREE.Object3D) {
	const replacedMaterials = new Set<THREE.Material>();
	const bindGlazing = (material: THREE.Material) => {
		if (material === matGlass) return material;
		const physical = material as THREE.MeshPhysicalMaterial;
		if (!physical.isMeshPhysicalMaterial || physical.transmission <= 0)
			return material;
		replacedMaterials.add(material);
		return matGlass;
	};
	root.traverse((child) => {
		const mesh = child as THREE.Mesh;
		if (mesh.isMesh) {
			// Clones share the cached source geometry; a rebuild disposing a
			// clone must not free the buffers every later clone draws from.
			SHARED_GEOMETRIES.add(mesh.geometry);
			child.castShadow = true;
			child.receiveShadow = true;
			mesh.material = Array.isArray(mesh.material)
				? mesh.material.map(bindGlazing)
				: bindGlazing(mesh.material);
		}
	});
	// The imported source no longer references these materials. Library glass
	// has its own lifetime and must never be disposed by the asset loader.
	replacedMaterials.forEach((material) => material.dispose());
	return root;
}

export function getPremiumModuleBaseSize(id: PremiumDetailId) {
	return DEFAULT_PREMIUM_SIZES[id];
}

export function loadPremiumModuleSource(
	assetPath: string,
): Promise<THREE.Object3D> {
	const cached = premiumModuleCache.get(assetPath);
	if (cached) {
		return cached;
	}

	const pending = new Promise<THREE.Object3D>((resolve, reject) => {
		loader.load(
			assetPath,
			(gltf) => resolve(preparePremiumModule(gltf.scene)),
			undefined,
			reject,
		);
	});

	premiumModuleCache.set(assetPath, pending);
	return pending;
}

export function resetPremiumModuleCache() {
	premiumModuleCache.clear();
}

const ATTACHED_KEY = "premiumSlotAttached";

function isUnder(object: THREE.Object3D, root: THREE.Object3D) {
	for (let node: THREE.Object3D | null = object; node; node = node.parent) {
		if (node === root) return true;
	}
	return false;
}

/**
 * Swap every not-yet-swapped premium slot under `root` for its asset. Safe to
 * call after any rebuild: slots already swapped are skipped, and a slot whose
 * subsystem was rebuilt (or a root replaced) while the asset downloaded keeps
 * its procedural part.
 */
export function attachPremiumSlots(
	root: THREE.Object3D,
	isCurrentRoot: (root: THREE.Object3D) => boolean,
	onAssetLoaded: (premiumObject: THREE.Object3D) => void,
	load: (
		assetPath: string,
	) => Promise<THREE.Object3D> = loadPremiumModuleSource,
): void {
	const slots: THREE.Object3D[] = [];
	root.traverse((object) => {
		if (readPremiumSlot(object) && !object.userData[ATTACHED_KEY]) {
			slots.push(object);
		}
	});
	for (const slot of slots) {
		const { assetPath, origin } = readPremiumSlot(slot) as NonNullable<
			ReturnType<typeof readPremiumSlot>
		>;
		slot.userData[ATTACHED_KEY] = true;
		load(assetPath)
			.then((source) => {
				const parent = slot.parent;
				if (!parent || !isCurrentRoot(root) || !isUnder(slot, root)) return;
				const premiumObject = source.clone(true);
				premiumObject.name = `${slot.name}/premium`;
				premiumObject.position
					.set(...origin)
					.applyQuaternion(slot.quaternion)
					.multiply(slot.scale)
					.add(slot.position);
				premiumObject.quaternion.copy(slot.quaternion);
				premiumObject.scale.copy(slot.scale);
				parent.add(premiumObject);
				slot.visible = false;
				onAssetLoaded(premiumObject);
			})
			.catch(() => {
				// Keep the procedural part visible.
				slot.userData[ATTACHED_KEY] = false;
			});
	}
}
